import { DialogModal } from 'components/common';
import {
  ColumnActivePanel,
  ColumnAddedPanel,
  SidePanel,
} from 'components/core';
import { findLast, isEqual, kebabCase, last } from 'lodash';
import { useContext, useEffect, useMemo, useState } from 'react';
import { ORDER_TABLE_HEADERS } from 'utils/constants/Order';
import { SHIPMENT_TABLE_HEADERS } from 'utils/constants/Shipment';
import { ColumnContext, ViewContext } from 'utils/context';
import {
  IDisplayOptions,
  TActiveColumns,
} from 'utils/context/ViewContext/types';
import { usePermissions } from 'utils/hooks';

import { CSS, Flex, Icon, IconButton } from '@project44/manifest-ds';
import { useTranslation } from '@project44/translations-utils';

import {
  buildManifest,
  matchColumns,
  matchSort,
  toggleGroupConfig,
  toggleGroupConfigExtended,
} from './utils';

type Panel = 'TOGGLE_COLUMNS' | 'REF_KEYS';

const PANEL_TITLE: Record<Panel, string> = {
  TOGGLE_COLUMNS: 'core.column',
  REF_KEYS: 'core.addReferenceKey',
};

interface IColumnPanel {
  isOrder?: boolean;
  onClose: () => void;
  style: CSS;
  bannerHeight?: string;
  updateColumns: (displayOptions: IDisplayOptions) => void;
}

const getKeys = (columns?: TActiveColumns) =>
  columns
    ? columns.filter(({ id }) => /ref-/i.test(id)).map(({ label }) => label)
    : [];

function ColumnPanel({
  isOrder = false,
  onClose,
  style,
  bannerHeight,
  updateColumns,
}: IColumnPanel) {
  const { t } = useTranslation();
  const { viewType } = useContext(ViewContext);
  const { displayOptions } = useContext(ColumnContext);
  const activeColumns = useMemo(
    () => displayOptions?.activeColumns || [],
    [displayOptions?.activeColumns],
  );

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [refKeys, setRefKeys] = useState(getKeys(activeColumns));
  const [visiblePanel, setVisiblePanel] = useState<Panel>('TOGGLE_COLUMNS');
  const permissions = usePermissions();

  useEffect(() => {
    const added = getKeys(activeColumns);
    setRefKeys(added);
  }, [activeColumns]);

  const triggerWarning = (callback: () => void) => {
    if (!isEqual(refKeys, getKeys(activeColumns))) {
      setIsModalOpen(true);
    } else {
      callback();
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleSelectKey = (refKey: string) => (isSelected?: boolean) => {
    if (isSelected) {
      setRefKeys((prev) => [...prev, refKey]);
    } else {
      setRefKeys((prev) => prev.filter((sel) => sel !== refKey));
    }
  };

  const handleGoBack = () => {
    triggerWarning(handleReturn);
  };

  const handleClose = () => {
    triggerWarning(() => {
      handleReturn();
      onClose();
    });
  };

  const handleReturn = () => {
    setIsModalOpen(false);
    setRefKeys(getKeys(activeColumns));
    setVisiblePanel('TOGGLE_COLUMNS');
  };

  const handleAddedColumns = (added: string[]) => {
    const colIds = activeColumns.map(({ id }) => id);
    const refIds = added.map((label) => `ref-${kebabCase(label)}`);
    const next = activeColumns.filter(
      ({ id }) => !/ref-/i.test(id) || refIds.includes(id),
    );
    added
      .map((label) => ({
        id: `ref-${kebabCase(label)}`,
        label,
        enabled: true,
      }))
      .forEach((column) => {
        if (!colIds.includes(column.id)) {
          next.push(column);
        }
      });

    updateColumns({ activeColumns: next });
  };

  const handleToggleColumns = (columnId: string) => {
    if (viewType === 'order') {
      const activeColumnsCopy = Array.from(activeColumns);
      const columnIdx = activeColumnsCopy.findIndex(
        (col) => col.id === columnId,
      );
      const nextColumnState = !activeColumnsCopy[columnIdx]?.enabled;

      const grabbedColumn = activeColumnsCopy.splice(columnIdx, 1)[0];

      grabbedColumn.enabled = nextColumnState;

      if (nextColumnState) {
        const lastEnabledColumn = findLast(
          activeColumnsCopy,
          (col) => col.enabled,
        );
        const idx = activeColumnsCopy.findIndex(
          (col) =>
            col.id === (lastEnabledColumn?.id || last(activeColumnsCopy)?.id),
        );
        activeColumnsCopy.splice(idx + 1, 0, grabbedColumn);
      } else {
        const firstDisabledColumn = activeColumnsCopy.find(
          (col) => !col.enabled,
        );
        const idx = activeColumnsCopy.findIndex(
          (col) =>
            col.id === (firstDisabledColumn?.id || last(activeColumns)?.id),
        );
        activeColumnsCopy.splice(idx, 0, grabbedColumn);
      }

      updateColumns({ activeColumns: activeColumnsCopy });

      return;
    }

    const forceSingleColumns = !permissions?.flags?.featureExc1914
      ? ['origin']
      : [];
    const {
      groups,
      getFirstDisabledColumn,
      getLastEnabledColumn,
      getGroupId,
      activeColumnsCopy,
    } = buildManifest(activeColumns, {
      forceSingleColumns,
    });

    const columnIdx = activeColumnsCopy.findIndex((col) => col.id === columnId);
    const nextColumnState = !activeColumnsCopy[columnIdx].enabled;

    activeColumnsCopy[columnIdx].enabled = nextColumnState;

    const columnGroupId = getGroupId(columnId);

    if (columnGroupId) {
      if (nextColumnState) {
        if (forceSingleColumns.includes(columnGroupId)) {
          const grabbedGroup = activeColumnsCopy.splice(
            columnIdx,
            groups[columnGroupId].columns.length,
          );

          const lastEnabledColumn = getLastEnabledColumn();
          const idx = activeColumnsCopy.findIndex(
            (col) => col.id === lastEnabledColumn,
          );
          activeColumnsCopy.splice(idx + 1, 0, ...grabbedGroup);
        }
      } else {
        const hasGroupBecomeDisabled = activeColumnsCopy
          .slice(columnIdx, columnIdx + groups[columnGroupId].columns.length)
          .every((col) => !col.enabled);

        if (hasGroupBecomeDisabled) {
          const grabbedGroup = activeColumnsCopy.splice(
            columnIdx,
            groups[columnGroupId].columns.length,
          );
          const firstDisabled = getFirstDisabledColumn();

          const idx = activeColumnsCopy.findIndex(
            (col) => col.id === firstDisabled,
          );

          activeColumnsCopy.splice(idx, 0, ...grabbedGroup);
        }
      }
    } else {
      const grabbedColum = activeColumnsCopy.splice(columnIdx, 1)[0];
      if (nextColumnState) {
        const lastEnabledColumn = getLastEnabledColumn();
        const idx = activeColumnsCopy.findIndex(
          (col) => col.id === lastEnabledColumn,
        );
        activeColumnsCopy.splice(idx + 1, 0, grabbedColum);
      } else {
        const firstDisabledColumn = getFirstDisabledColumn();
        const idx = activeColumnsCopy.findIndex(
          (col) => col.id === firstDisabledColumn,
        );
        activeColumnsCopy.splice(idx, 0, grabbedColum);
      }
    }

    updateColumns({ activeColumns: activeColumnsCopy });
  };

  const handleToggleGroup = (columnIds: string[], currentState: boolean) => {
    const nextColumnsState = !currentState;

    const { getLastEnabledColumn, getFirstDisabledColumn } =
      buildManifest(activeColumns);

    const activeColumnsCopy = Array.from(activeColumns);

    const firstColumnIdx = activeColumnsCopy.findIndex(
      (col) => col.id === columnIds[0],
    );
    const grabbedGroup = activeColumnsCopy.splice(
      firstColumnIdx,
      columnIds.length,
    );

    grabbedGroup.forEach((col) => {
      col.enabled = nextColumnsState;
    });

    if (nextColumnsState) {
      const lastEnabledColumn = getLastEnabledColumn();
      const idx = activeColumnsCopy.findIndex(
        (col) => col.id === lastEnabledColumn,
      );
      activeColumnsCopy.splice(idx + 1, 0, ...grabbedGroup);
    } else {
      const firstDisabledColumn = getFirstDisabledColumn();
      const idx = activeColumnsCopy.findIndex(
        (col) => col.id === firstDisabledColumn,
      );
      activeColumnsCopy.splice(idx, 0, ...grabbedGroup);
    }

    updateColumns({ activeColumns: activeColumnsCopy });
  };

  const handleSort = (active: string[]) => {
    const addActiveGroupIds = !permissions.flags?.featureExc1914
      ? active.map((item) =>
          toggleGroupConfig.hasOwnProperty(item)
            ? toggleGroupConfig[item]?.map(({ id }) => id)
            : item,
        )
      : active.map((item) =>
          toggleGroupConfigExtended.hasOwnProperty(item)
            ? toggleGroupConfigExtended[item]?.map(({ id }) => id)
            : item,
        );
    const activeGroupIds = addActiveGroupIds.flat();
    const baseId = isOrder ? 'order-number' : 'shipment-id';
    const inactive = activeColumns.filter(({ enabled }) => !enabled);

    const activeIds = activeColumns
      .filter(({ enabled }) => enabled)
      .map(({ id }) => id);

    const sort = [baseId, ...activeGroupIds].map((id) => ({
      id,
      label: id,
      enabled: activeIds.includes(id),
    }));

    const next = matchSort(activeColumns, [...sort, ...inactive], permissions);

    updateColumns({ activeColumns: next });
  };

  const [headers] = useMemo(
    () =>
      matchColumns(
        isOrder ? ORDER_TABLE_HEADERS : SHIPMENT_TABLE_HEADERS,
        [],
        activeColumns,
        permissions,
      ),
    [activeColumns, permissions, isOrder],
  );

  return (
    <SidePanel
      title={t(PANEL_TITLE[visiblePanel])}
      onClose={visiblePanel === 'REF_KEYS' ? handleClose : onClose}
      style={style}
      bannerHeight={bannerHeight}
      startIcon={
        visiblePanel === 'REF_KEYS' ? (
          <IconButton
            aria-label={t('core.columnSelectBack')}
            onPress={handleGoBack}
            variant="tertiary"
          >
            <Icon icon="chevron_left" />
          </IconButton>
        ) : undefined
      }
    >
      <Flex orientation="vertical" css={{ flexGrow: 1, overflowY: 'auto' }}>
        {visiblePanel === 'TOGGLE_COLUMNS' ? (
          <ColumnActivePanel
            handleToggle={handleToggleColumns}
            handleToggleGroup={handleToggleGroup}
            isOrder={isOrder}
            toggleOptions={activeColumns
              .filter(({ enabled }) => !enabled)
              .map(({ id }) => id)}
            visibleOptions={headers}
            onClose={onClose}
            onSort={handleSort}
            onPress={!isOrder ? () => setVisiblePanel('REF_KEYS') : undefined}
          />
        ) : (
          <ColumnAddedPanel
            addedColumns={refKeys}
            onSelectKey={handleSelectKey}
            onCancel={handleReturn}
            onConfirm={() => {
              handleAddedColumns(refKeys);
              setVisiblePanel('TOGGLE_COLUMNS');
            }}
          />
        )}
      </Flex>
      <DialogModal
        title={t('core.unsavedChangesWarningTitle')}
        description={t('core.unsavedChangesWarningDescription')}
        onCancel={handleCloseModal}
        onConfirm={handleReturn}
        isOpen={isModalOpen}
      />
    </SidePanel>
  );
}

export default ColumnPanel;
